import { useEffect, useRef, useState, useCallback } from "react";

const WS_BASE = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws") + "/api/ws";
const PING_INTERVAL = 25000; // keep-alive to survive proxy idle timeouts (~100s)

export function useOverlayWS(overlayId, role, key, onMessage, onReconnect) {
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const attempt = useRef(0);
  const closedRef = useRef(false);
  const everOpenRef = useRef(false);
  const pingRef = useRef(null);
  const cbRef = useRef(onMessage);
  cbRef.current = onMessage;
  const reconnRef = useRef(onReconnect);
  reconnRef.current = onReconnect;

  const stopPing = () => {
    if (pingRef.current) {
      clearInterval(pingRef.current);
      pingRef.current = null;
    }
  };

  const connect = useCallback(() => {
    if (!overlayId) return;
    let url = `${WS_BASE}/${overlayId}?role=${role}`;
    if (key) url += `&key=${encodeURIComponent(key)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onopen = () => {
      setConnected(true);
      // On any reconnect (not the very first open), let consumers resync state.
      if (everOpenRef.current) reconnRef.current?.();
      everOpenRef.current = true;
      attempt.current = 0;
      stopPing();
      pingRef.current = setInterval(() => {
        if (wsRef.current?.readyState === 1) {
          try { wsRef.current.send(JSON.stringify({ type: "ping" })); } catch {}
        }
      }, PING_INTERVAL);
    };
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data?.type === "pong") return;
        cbRef.current?.(data);
      } catch {}
    };
    ws.onclose = () => {
      setConnected(false);
      stopPing();
      if (closedRef.current) return;
      const delay = Math.min(1000 * 2 ** attempt.current, 8000);
      attempt.current += 1;
      setTimeout(() => {
        if (!closedRef.current) connect();
      }, delay);
    };
    ws.onerror = () => ws.close();
  }, [overlayId, role, key]);

  useEffect(() => {
    closedRef.current = false;
    everOpenRef.current = false;
    connect();
    return () => {
      closedRef.current = true;
      stopPing();
      wsRef.current?.close();
    };
  }, [connect]);

  const send = useCallback((msg) => {
    if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify(msg));
  }, []);

  return { connected, send };
}
